import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import SignaturePad from '../components/SignaturePad';
import HomeButton from '../components/HomeButton';
import { ArrowLeft, Edit, Download, CheckCircle, MapPin, Clock, Calendar, Briefcase, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrabajoDetalle = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();

  const [trabajo, setTrabajo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [completando, setCompletando] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    loadTrabajo();
  }, [id]);

  const loadTrabajo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/trabajos/${id}`);
      setTrabajo(response.data);
    } catch (err) {
      setError('Error al cargar el trabajo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompletarTrabajo = async (firmaBase64) => {
    try {
      setCompletando(true);
      await axios.patch(`${API}/trabajos/${id}/completar`, {
        firma_base64: firmaBase64,
        observaciones: trabajo.observaciones,
        horas_trabajadas: trabajo.horas_trabajadas
      });
      setShowSignaturePad(false);
      loadTrabajo();
    } catch (err) {
      setError('Error al completar el trabajo');
      console.error(err);
    } finally {
      setCompletando(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/trabajos/${id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trabajo_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Error al descargar el PDF');
      console.error(err);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'en_progreso':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'completado':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando trabajo...</p>
        </div>
      </div>
    );
  }

  if (!trabajo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-12 text-center">
            <p className="text-gray-600">Trabajo no encontrado</p>
            <Button onClick={() => navigate('/tecnico')} className="mt-4">
              Volver al Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-6" data-testid="trabajo-detalle">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
            <HomeButton />
          </div>

          <div className="flex gap-2">
            {(user?.rol === 'tecnico' || user?.rol === 'administrador') && trabajo.estado !== 'completado' && (
              <Button
                variant="outline"
                onClick={() => {
                  const editPath = user?.rol === 'administrador' 
                    ? `/admin/trabajo/${id}/editar` 
                    : `/tecnico/trabajo/${id}/editar`;
                  navigate(editPath);
                }}
                data-testid="edit-button"
              >
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </Button>
            )}
            <Button onClick={handleDownloadPDF} data-testid="download-pdf-button">
              <Download className="mr-2 h-4 w-4" />
              Descargar PDF
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Main Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div className="flex-1">
                {trabajo.folio && (
                  <p className="text-red-600 font-bold text-sm mb-1">Folio: {trabajo.folio}</p>
                )}
                <CardTitle className="text-3xl mb-2">{trabajo.titulo}</CardTitle>
                <p className="text-gray-600">{trabajo.descripcion}</p>
              </div>
              <Badge className={`${getEstadoColor(trabajo.estado)} text-lg px-4 py-2`}>
                {trabajo.estado === 'pendiente' && 'Pendiente'}
                {trabajo.estado === 'en_progreso' && 'En Progreso'}
                {trabajo.estado === 'completado' && 'Completado'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Información General */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Información General</h3>
                
                {trabajo.tecnico_asignado_nombre && (
                  <div className="flex items-start gap-2">
                    <Briefcase className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Técnico</p>
                      <p className="font-medium">{trabajo.tecnico_asignado_nombre}</p>
                    </div>
                  </div>
                )}

                {trabajo.cliente_nombre && (
                  <div className="flex items-start gap-2">
                    <Briefcase className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Cliente</p>
                      <p className="font-medium">{trabajo.cliente_nombre}</p>
                    </div>
                  </div>
                )}

                {trabajo.direccion && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Dirección</p>
                      <p className="font-medium">{trabajo.direccion}</p>
                    </div>
                  </div>
                )}

                {trabajo.ubicacion_gps && (
                  <div className="flex items-start gap-2">
                    <MapPin className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Coordenadas GPS</p>
                      <p className="font-medium text-xs">
                        Lat: {trabajo.ubicacion_gps.lat.toFixed(6)}, Lng: {trabajo.ubicacion_gps.lng.toFixed(6)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Fechas y Tiempos */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg border-b pb-2">Fechas y Tiempos</h3>
                
                <div className="flex items-start gap-2">
                  <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Fecha Creación</p>
                    <p className="font-medium">{formatFecha(trabajo.fecha_creacion)}</p>
                  </div>
                </div>

                {trabajo.fecha_inicio && (
                  <div className="flex items-start gap-2">
                    <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Fecha Inicio</p>
                      <p className="font-medium">{formatFecha(trabajo.fecha_inicio)}</p>
                    </div>
                  </div>
                )}

                {trabajo.fecha_finalizacion && (
                  <div className="flex items-start gap-2">
                    <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Fecha Finalización</p>
                      <p className="font-medium">{formatFecha(trabajo.fecha_finalizacion)}</p>
                    </div>
                  </div>
                )}

                {trabajo.horas_trabajadas && (
                  <div className="flex items-start gap-2">
                    <Clock className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Horas Trabajadas</p>
                      <p className="font-medium">{trabajo.horas_trabajadas} horas</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Materiales y Observaciones */}
        {(trabajo.materiales_usados || trabajo.observaciones) && (
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="space-y-4">
                {trabajo.materiales_usados && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Materiales Usados</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{trabajo.materiales_usados}</p>
                  </div>
                )}

                {trabajo.observaciones && (
                  <div>
                    <h3 className="font-semibold text-lg mb-2">Observaciones</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{trabajo.observaciones}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fotografías */}
        {trabajo.fotos && trabajo.fotos.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Fotografías del Trabajo ({trabajo.fotos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {trabajo.fotos.map((foto, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedPhoto(foto)}
                    data-testid={`photo-${index}`}
                  >
                    <img
                      src={foto}
                      alt={`Foto ${index + 1}`}
                      className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition-shadow"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 font-medium">Ver</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fotografías de Boletas */}
        {trabajo.fotos_boletas && trabajo.fotos_boletas.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Fotografías de Boletas ({trabajo.fotos_boletas.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {trabajo.fotos_boletas.map((foto, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedPhoto(foto)}
                  >
                    <img
                      src={foto}
                      alt={`Boleta ${index + 1}`}
                      className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition-shadow"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 font-medium">Ver</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fotografías de Equipos */}
        {trabajo.fotos_equipos && trabajo.fotos_equipos.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Fotografías de Equipos Usados ({trabajo.fotos_equipos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {trabajo.fotos_equipos.map((foto, index) => (
                  <div
                    key={index}
                    className="relative group cursor-pointer"
                    onClick={() => setSelectedPhoto(foto)}
                  >
                    <img
                      src={foto}
                      alt={`Equipo ${index + 1}`}
                      className="w-full h-40 object-cover rounded-lg border border-gray-200 hover:shadow-lg transition-shadow"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 font-medium">Ver</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Firma */}
        {trabajo.firma_base64 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Firma de Recepción</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 p-4 rounded-lg inline-block">
                <img
                  src={trabajo.firma_base64}
                  alt="Firma"
                  className="max-w-md border border-gray-300 bg-white"
                  data-testid="signature-image"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Botón Completar Trabajo */}
        {user?.rol === 'tecnico' && trabajo.estado !== 'completado' && !trabajo.firma_base64 && (
          <Card>
            <CardContent className="pt-6">
              <Button
                onClick={() => setShowSignaturePad(true)}
                size="lg"
                className="w-full"
                data-testid="complete-work-button"
              >
                <CheckCircle className="mr-2 h-5 w-5" />
                Completar Trabajo con Firma
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de foto grande */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedPhoto(null)}
          data-testid="photo-modal"
        >
          <div className="max-w-4xl max-h-full">
            <img
              src={selectedPhoto}
              alt="Foto ampliada"
              className="max-w-full max-h-screen object-contain"
            />
          </div>
        </div>
      )}

      {/* Signature Pad */}
      {showSignaturePad && (
        <SignaturePad
          onSave={handleCompletarTrabajo}
          onCancel={() => setShowSignaturePad(false)}
        />
      )}
    </div>
  );
};

export default TrabajoDetalle;
